import { action, computed, makeObservable } from 'mobx';

import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { UserStore } from '@/configs/store';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { ITaskInfo } from '@/entities/task/model/client';
import { TaskInfoModel } from '@/entities/task/model/TaskInfoModel';
import { LogsMode } from '@/features/logsMode';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { ILocalStore } from '@/types/store';
import { formatError } from '@/utils/formatError';
import { transformTimelogsTaskInfo } from '@/utils/transformTimelog';

/**
 * модель для работы с избранными задачами
 * и текущей задачей (отображается в DetailsDrawer)
 */
export class TaskStore extends LocalStore implements ILocalStore {
  private readonly _task = new ValueModel<ITaskInfo | null>(null);
  private readonly _favoriteTasks = new ValueModel<ITaskInfo[]>([]);

  private readonly _userStore: UserStore;
  private readonly _alertsStore: AlertsStore;

  private readonly _loadingStage = new LoadingStageModel();
  private readonly _mode: LogsMode;

  constructor({
    userStore,
    alertsStore,
    mode,
  }: {
    userStore: UserStore;
    alertsStore: AlertsStore;
    mode: LogsMode;
  }) {
    super();

    this._userStore = userStore;
    this._alertsStore = alertsStore;
    this._mode = mode;

    makeObservable(this, {
      task: computed,
      loadingStage: computed,
      favoriteTasks: computed,
      setTask: action.bound,
      setFavoriteTasks: action.bound,
      loadTask: action.bound,
      loadFavoriteTasks: action.bound,
    });
  }

  get task() {
    return this._task.value;
  }

  get favoriteTasks() {
    return this._favoriteTasks.value;
  }

  get loadingStage() {
    return this._loadingStage;
  }

  setTask(task: ITaskInfo | null) {
    this._task.change(task);
  }

  setFavoriteTasks(tasks: ITaskInfo[]) {
    this._favoriteTasks.change(tasks);
  }

  async loadTask(taskKey: string) {
    if (!this._userStore.isAuthorized) return;

    this._loadingStage.loading();

    if (this._mode === LogsMode.v2) {
      await this._loadTaskV2(taskKey);

      return;
    }

    const response = await yandexTrackerApi.getTask({ taskKey });

    if ('details' in response) {
      this.setTask(null);
      this.loadingStage.error();

      const error = formatError(response);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });

      return;
    }

    const taskInfo: ITaskInfo = TaskInfoModel.fromJson(response);

    this.setTask(taskInfo);
    this._loadingStage.success();
  }

  async loadFavoriteTasks() {
    // В собственном учёте избранного задач нет: ручки нет, на фронте не собираем
    if (!this._userStore.isAuthorized || this._mode === LogsMode.v2) return;

    this._loadingStage.loading();

    const response = await yandexTrackerApi.getFavoriteTasks();

    if ('details' in response) {
      this.setFavoriteTasks([]);
      this.loadingStage.error();
      const error = formatError(response);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });

      return;
    }

    const favoriteTasks: ITaskInfo[] = response.map((task) => TaskInfoModel.fromJson(task));

    this.setFavoriteTasks(favoriteTasks);
    this._loadingStage.success();
  }

  private async _loadTaskV2(taskKey: string) {
    try {
      this.setTask(transformTimelogsTaskInfo(await timelogsApi.getTask(taskKey)));
      this._loadingStage.success();
    } catch (e) {
      this.setTask(null);
      this._loadingStage.error();

      const error = formatError(e);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });
    }
  }
}
